"use client";

import { Dialog, Transition } from "@headlessui/react";
import Image from "next/image";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { API_BASE_URL } from "@lib/config";
import { LuDownload, LuLoader, LuRefreshCw, LuX } from "react-icons/lu";
import { PiPantsThin } from "react-icons/pi";
import { CiCamera } from "react-icons/ci";
type VirtualTryOnDrawerProps = {
  open: boolean;
  onClose: () => void;
  product: any;
};
type Step = "upload" | "processing" | "result";
const ACCEPTED_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/heic", "image/heif", "image/avif"];
const VirtualTryOnDrawer = ({
  open,
  onClose,
  product
}: VirtualTryOnDrawerProps) => {
  const [step, setStep] = useState<Step>("upload");
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const previewUrlRef = useRef<string | null>(null);
  const [previewUrl, setPreviewUrlState] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [showMobileUploadOptions, setShowMobileUploadOptions] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const sessionPhotoAbortControllerRef = useRef<AbortController | null>(null);
  const hasLoadedSessionPhotoRef = useRef(false);
  const [isLoadingSessionPhoto, setIsLoadingSessionPhoto] = useState(false);
  const setPreviewUrl = useCallback((next: string | null) => {
    setPreviewUrlState(prev => {
      if (prev && prev !== next) {
        URL.revokeObjectURL(prev);
      }
      previewUrlRef.current = next;
      return next;
    });
  }, []);
  useEffect(() => {
    return () => {
      const existing = previewUrlRef.current;
      if (existing) {
        URL.revokeObjectURL(existing);
      }
    };
  }, []);
  const heroImage = useMemo(() => {
    if (!Array.isArray(product?.images) || product.images.length === 0) {
      return null;
    }
    return product.images[0]?.url || null;
  }, [product]);
  const resetState = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    if (sessionPhotoAbortControllerRef.current) {
      sessionPhotoAbortControllerRef.current.abort();
      sessionPhotoAbortControllerRef.current = null;
    }
    setError(null);
    setSelectedFile(null);
    setResultUrl(null);
    setPreviewUrl(null);
    setDragActive(false);
    setStep("upload");
    hasLoadedSessionPhotoRef.current = false;
    setIsLoadingSessionPhoto(false);
    setShowMobileUploadOptions(false);
  }, [setPreviewUrl]);
  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open, resetState]);
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    let existingSessionId: string | null = null;
    try {
      existingSessionId = window.localStorage.getItem("virtualTryOnSessionId");
    } catch (error_0) {
      console.warn("Failed to access localStorage for virtual try-on session", error_0);
    }
    if (!existingSessionId) {
      const generatedSessionId = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function" ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      try {
        window.localStorage.setItem("virtualTryOnSessionId", generatedSessionId);
      } catch (error_1) {
        console.warn("Failed to persist virtual try-on session id", error_1);
      }
      existingSessionId = generatedSessionId;
    }
    setSessionId(existingSessionId);
  }, []);
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const mediaQuery = window.matchMedia("(pointer: coarse)");
    const updateIsMobile = () => {
      setIsMobile(mediaQuery.matches);
    };
    updateIsMobile();
    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", updateIsMobile);
      return () => mediaQuery.removeEventListener("change", updateIsMobile);
    }
    if (typeof mediaQuery.addListener === "function") {
      mediaQuery.addListener(updateIsMobile);
      return () => mediaQuery.removeListener(updateIsMobile);
    }
    return undefined;
  }, []);
  const persistSessionPhoto = useCallback(async (file: File) => {
    if (!sessionId) {
      return;
    }
    try {
      const endpoint = `${API_BASE_URL}/public/virtual-try-on/sessions/${encodeURIComponent(sessionId)}/photo`;
      const formData = new FormData();
      formData.append("photo", file);
      const response = await fetch(endpoint, {
        method: "POST",
        body: formData
      });
      if (!response.ok) {
        throw new Error(`Failed to persist session photo (${response.status})`);
      }
    } catch (error_2) {
      console.warn("Failed to store virtual try-on session photo", error_2);
    }
  }, [sessionId]);
  const handleFileSelection = useCallback((file_0: File | null, options?: {
    persist?: boolean;
  }) => {
    if (!file_0) return;
    const shouldPersist = options?.persist !== false;
    if (!file_0.type.startsWith("image/")) {
      setError("Formato no soportado. Subí una imagen.");
      return;
    }
    if (ACCEPTED_TYPES.length && !ACCEPTED_TYPES.includes(file_0.type)) {
      setError("Este formato de imagen no es compatible.");
      return;
    }
    if (file_0.size > 25 * 1024 * 1024) {
      setError("La imagen es muy pesada. Probá con un archivo menor a 25MB.");
      return;
    }
    setError(null);
    setSelectedFile(file_0);
    hasLoadedSessionPhotoRef.current = true;
    const nextPreview = URL.createObjectURL(file_0);
    setPreviewUrl(nextPreview);
    if (shouldPersist) {
      void persistSessionPhoto(file_0);
    }
  }, [persistSessionPhoto, setPreviewUrl]);
  const handleFileInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file_1 = event.target.files?.[0] || null;
    if (event.target) {
      event.target.value = "";
    }
    handleFileSelection(file_1);
  }, [handleFileSelection]);
  const handleDrop = useCallback((event_0: React.DragEvent<HTMLDivElement>) => {
    event_0.preventDefault();
    event_0.stopPropagation();
    setDragActive(false);
    const file_2 = event_0.dataTransfer?.files?.[0] || null;
    handleFileSelection(file_2);
  }, [handleFileSelection]);
  const handleDragOver = useCallback((event_1: React.DragEvent<HTMLDivElement>) => {
    event_1.preventDefault();
    event_1.stopPropagation();
    setDragActive(true);
  }, []);
  const handleDragLeave = useCallback((event_2: React.DragEvent<HTMLDivElement>) => {
    event_2.preventDefault();
    event_2.stopPropagation();
    setDragActive(false);
  }, []);
  const handleGenerate = useCallback(async () => {
    if (!selectedFile) {
      setError("Subí una foto para continuar.");
      return;
    }
    setError(null);
    setStep("processing");
    const controller = new AbortController();
    abortControllerRef.current = controller;
    try {
      const formData_0 = new FormData();
      formData_0.append("photo", selectedFile);
      if (sessionId) {
        formData_0.append("sessionId", sessionId);
      }
      const productId = product?._id || product?.id;
      const response_0 = await fetch(`${API_BASE_URL}/public/products/${encodeURIComponent(String(productId))}/virtual-try-on`, {
        method: "POST",
        body: formData_0,
        signal: controller.signal
      });
      const data = await response_0.json().catch(() => null);
      if (!response_0.ok || !data) {
        const message_0 = typeof data?.error === "string" ? data.error : "No se pudo generar el outfit virtual.";
        throw new Error(message_0);
      }
      setResultUrl(typeof data.resultImageUrl === "string" ? data.resultImageUrl : null);
      setStep("result");
    } catch (err) {
      if ((err as Error).name === "AbortError") {
        return;
      }
      const message = err instanceof Error ? err.message : "Ocurrió un error inesperado.";
      setError(message);
      setStep("upload");
    } finally {
      abortControllerRef.current = null;
    }
  }, [product, selectedFile, sessionId]);
  const handleReset = useCallback(() => {
    setSelectedFile(null);
    setResultUrl(null);
    setPreviewUrl(null);
    setStep("upload");
    setError(null);
    setShowMobileUploadOptions(false);
  }, [setPreviewUrl]);
  const closeDrawer = useCallback(() => {
    onClose();
  }, [onClose]);
  useEffect(() => {
    if (!open || !sessionId || selectedFile || hasLoadedSessionPhotoRef.current) {
      return;
    }
    const controller_0 = new AbortController();
    if (sessionPhotoAbortControllerRef.current) {
      sessionPhotoAbortControllerRef.current.abort();
    }
    sessionPhotoAbortControllerRef.current = controller_0;
    setIsLoadingSessionPhoto(true);
    const loadStoredPhoto = async () => {
      try {
        const response_1 = await fetch(`${API_BASE_URL}/public/virtual-try-on/sessions/${encodeURIComponent(sessionId)}`, {
          signal: controller_0.signal
        });
        if (!response_1.ok) {
          return;
        }
        const payload = (await response_1.json().catch(() => null)) as {
          photoUrl?: string | null;
          fileName?: string | null;
        } | null;
        if (!payload?.photoUrl) {
          return;
        }
        const photoResponse = await fetch(payload.photoUrl, {
          signal: controller_0.signal
        });
        if (!photoResponse.ok) {
          return;
        }
        const blob = await photoResponse.blob();
        if (controller_0.signal.aborted) {
          return;
        }
        const inferredName = typeof payload.fileName === "string" && payload.fileName.trim() ? payload.fileName.trim() : "virtual-try-on-session-photo.webp";
        const inferredType = blob.type || "image/webp";
        const restoredFile = new File([blob], inferredName, {
          type: inferredType
        });
        handleFileSelection(restoredFile, {
          persist: false
        });
      } catch (error_3) {
        if ((error_3 as Error).name !== "AbortError") {
          console.warn("Failed to load stored virtual try-on photo", error_3);
        }
      } finally {
        if (sessionPhotoAbortControllerRef.current === controller_0) {
          sessionPhotoAbortControllerRef.current = null;
        }
        setIsLoadingSessionPhoto(false);
      }
    };
    void loadStoredPhoto();
    return () => {
      controller_0.abort();
      if (sessionPhotoAbortControllerRef.current === controller_0) {
        sessionPhotoAbortControllerRef.current = null;
      }
      setIsLoadingSessionPhoto(false);
    };
  }, [handleFileSelection, open, sessionId, selectedFile]);
  const handleUploadButtonClick = useCallback(() => {
    if (isMobile) {
      setShowMobileUploadOptions(true);
    } else {
      fileInputRef.current?.click();
    }
  }, [isMobile]);
  const handleCameraSelection = useCallback(() => {
    setShowMobileUploadOptions(false);
    cameraInputRef.current?.click();
  }, []);
  const handleGallerySelection = useCallback(() => {
    setShowMobileUploadOptions(false);
    fileInputRef.current?.click();
  }, []);
  const renderUploadStep = () => <div className="space-y-6">
      <div className={`rounded-3xl border-1 border-dashed ${dragActive ? "border-black bg-black/5" : "border-white"} transition`} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
        <input ref={fileInputRef} type="file" accept="image/*" className="hidden" multiple={false} onChange={handleFileInputChange} />
        <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden" multiple={false} onChange={handleFileInputChange} />
        <div className="grid gap-6 p-6 sm:grid-cols-[1.1fr_0.9fr]">
          <div className="flex flex-col items-center justify-center gap-4 text-center sm:items-start sm:text-left">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-zinc-100 text-black">
              <CiCamera className="h-4 w-4" />
            </div>
            <div>
              <p className="text-lg font-lealti-light text-black">Subí tu foto</p>
              <p className="mt-1 text-xs text-gray-500 font-lealti-light">
                Elegí una foto de cuerpo completo con buena iluminación para obtener un resultado más realista.
              </p>
            </div>
            {selectedFile && <div className="rounded-full bg-black px-5 py-2 text-sm font-lealti-light uppercase tracking-wide text-white">
                {selectedFile.name}
              </div>}
            <div className="flex flex-wrap items-center justify-center gap-3 sm:justify-start">
              <button type="button" onClick={handleUploadButtonClick} className="rounded-full border border-black px-8 py-2 text-xs font-lealti-light uppercase tracking-wide text-black transition hover:bg-black hover:text-white">
                Subir Foto
              </button>
              {selectedFile && <button type="button" onClick={handleReset} className="rounded-full border border-transparent px-8 py-2 text-xs font-lealti-light uppercase tracking-wide text-gray-600 transition hover:border-gray-300 hover:text-black">
                  Quitar foto
                </button>}
            </div>
          </div>
          <div className="relative overflow-hidden rounded-xl border border-zinc-50 bg-zinc-50 aspect-[3/4] h-full min-h-[220px]">
            {previewUrl ? <Image src={previewUrl} alt="Vista previa de tu foto" fill className="object-cover" /> : <div className="flex h-full min-h-[220px] flex-col items-center justify-center gap-2 px-4 text-center text-sm text-gray-500">
                <span className="font-lealti-light text-base text-black">Vista previa</span>
                <span className="font-lealti-light text-xs ">
                  {isLoadingSessionPhoto ? "Estamos recuperando tu última foto..." : "Tu foto aparecerá aquí cuando la cargues."}
                </span>
              </div>}
          </div>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="rounded-2xl bg-gray-50 p-4 text-sm text-zinc-50">
          <p className="font-lealti text-gray-900">Consejos rápidos</p>
          <ul className="mt-3 space-y-2 text-xs text-gray-600 font-lealti-light">
            <li>• Evitá fondos muy cargados y asegurate de estar centrada/o en la foto.</li>
            <li>• Una postura relajada y de frente ayuda a obtener mejores resultados.</li>
            <li>• Usá ropa ajustada al cuerpo para que el ajuste sea más preciso.</li>
          </ul>
        </div>
        {heroImage && <div className="rounded-2xl bg-white p-4">
            <p className="text-xs font-lealti uppercase tracking-wide text-black text-center">Prenda seleccionada</p>
            <div className="relative mt-3 aspect-[3/4] overflow-hidden rounded-xl font-lealti-light">
              <Image src={heroImage} alt={`Referencia de ${product?.name ?? "el producto"}`} fill className="object-cover" />
            </div>
          </div>}
      </div>

      <button type="button" onClick={handleGenerate} disabled={!selectedFile} className="flex w-full items-center justify-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-lealti-light uppercase tracking-wide text-black transition hover:bg-black/90 hover:text-white disabled:cursor-not-allowed disabled:bg-zinc-50 disabled:text-zinc-500">
        <PiPantsThin className="h-5 w-5" />
        Iniciar probador virtual
      </button>
    </div>;
  const renderProcessingStep = () => <div className="flex flex-col items-center justify-center gap-4 py-16 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-black text-white">
        <LuLoader className="h-6 w-6 animate-spin" />
      </div>
      <p className="text-lg font-lealti text-black">Generando tu look personalizado…</p>
      <p className="max-w-sm text-sm text-gray-500 font-lealti-light">
        Estamos combinando tu foto con {product?.name ?? "la prenda"}. Este proceso puede tardar unos segundos.
      </p>
    </div>;
  const renderResultStep = () => <div className="space-y-6">
      <div className="relative overflow-hidden rounded-3xl bg-gray-100 aspect-[3/4] min-h-[220px]">
        {resultUrl ? <Image src={resultUrl} alt="Resultado del probador virtual" fill className="object-cover" /> : <div className="flex h-full w-full items-center justify-center text-sm text-gray-500">
            No pudimos mostrar la imagen generada.
          </div>}
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <button type="button" onClick={handleReset} className="flex items-center justify-center gap-2 rounded-full border border-black px-5 py-2.5 text-xs font-lealti-light uppercase tracking-wide text-black transition hover:bg-black hover:text-white">
          <LuRefreshCw className="h-5 w-5" />
          Probar con otra foto
        </button>
        {resultUrl && <a href={resultUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 rounded-full bg-black px-5 py-2.5 text-xs font-lealti-light uppercase tracking-wide text-white transition hover:bg-black/90">
            <LuDownload className="h-5 w-5" />
            Descargar imagen
          </a>}
      </div>
    </div>;
  return <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={closeDrawer}>
        <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-4 sm:pl-10">
              <Transition.Child as={Fragment} enter="transform transition ease-out duration-200" enterFrom="translate-x-full" enterTo="translate-x-0" leave="transform transition ease-in duration-150" leaveFrom="translate-x-0" leaveTo="translate-x-full">
                <Dialog.Panel className="pointer-events-auto w-screen max-w-xl">
                  <div className="flex h-full flex-col overflow-y-auto bg-white shadow-xl">
                    <div className="flex items-start justify-between gap-4 border-b px-6 py-5">
                      <div>
                        <Dialog.Title className="text-lg font-lealti-light text-gray-900">Probador Virtual</Dialog.Title>
                        <p className="mt-1 text-sm text-black font-lealti-light">
                          Probá {product?.name ?? "este producto"} con nuestro provador virtual subiendo tu foto.
                        </p>
                      </div>
                      <button type="button" onClick={closeDrawer} className="rounded-full border border-gray-200 p-2 text-gray-500 transition hover:border-black hover:text-black" aria-label="Cerrar probador virtual">
                        <LuX className="h-5 w-5" />
                      </button>
                    </div>

                    <div className="flex-1 space-y-6 px-6 py-6">
                      {error && <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                          {error}
                        </div>}

                      {step === "upload" && renderUploadStep()}
                      {step === "processing" && renderProcessingStep()}
                      {step === "result" && renderResultStep()}
                    </div>

                    <div className="border-t px-6 py-4 text-center text-xs text-gray-500 font-lealti-light">
                      Las imágenes generadas son solo a modo de referencia y pueden diferir del resultado final.
                    </div>

                    <div className=" py-4 text-center text-xs text-black font-lealti-light">
                      Powered by citivel.com
                    </div>
                    {showMobileUploadOptions && <div className="fixed inset-0 z-[60] flex flex-col justify-end bg-black/40 px-4 pb-6">
                        <div className="mx-auto w-full max-w-sm rounded-3xl bg-white p-5 shadow-2xl">
                          <p className="text-sm font-lealti text-black text-center">Elegí cómo subir tu foto</p>
                          <div className="mt-4 space-y-3">
                            <button type="button" onClick={handleCameraSelection} className="w-full rounded-full bg-black px-5 py-3 text-xs font-lealti-light uppercase tracking-wide text-white transition hover:bg-black/90">
                              Tomar una foto
                            </button>
                            <button type="button" onClick={handleGallerySelection} className="w-full rounded-full border border-black px-5 py-3 text-xs font-lealti-light uppercase tracking-wide text-black transition hover:bg-black hover:text-white">
                              Elegir desde galería
                            </button>
                          </div>
                          <button type="button" onClick={() => setShowMobileUploadOptions(false)} className="mt-4 w-full rounded-full border border-transparent px-5 py-2 text-xs font-lealti-light uppercase tracking-wide text-gray-500 transition hover:border-gray-200 hover:text-black">
                            Cancelar
                          </button>
                        </div>
                      </div>}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>;
};
export default VirtualTryOnDrawer;